import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Badge } from "../../../../shadcn/ui/badge";
import { Button } from "../../../../shadcn/ui/button";
import {
  Users,
  MapPin,
  Wifi,
  Coffee,
  Monitor,
  Car,
  Video,
  Phone,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SpaceGridView = ({ spaces, onSelectSpace }) => {
  const getAmenityIcon = amenity => {
    // Handle both string amenities (old format) and object amenities (new format)
    const amenityId = typeof amenity === "object" ? amenity.id : amenity.toLowerCase();

    const iconMap = {
      wifi: Wifi,
      coffee: Coffee,
      monitor: Monitor,
      parking: Car,
      video: Video,
      phone: Phone,
      power: Monitor, // Power outlets
      silence: Monitor, // Quiet zone
    };
    const IconComponent = iconMap[amenityId] || Monitor;
    return <IconComponent className="w-4 h-4" />;
  };

  const getAmenityName = amenity => {
    // Handle both string amenities (old format) and object amenities (new format)
    if (typeof amenity === "object") {
      return amenity.label || amenity.name;
    }
    return amenity.charAt(0).toUpperCase() + amenity.slice(1);
  };

  const getStatusBadge = status => {
    // Don't show badge for published status (it's the default state)
    if (!status || status.toLowerCase() === "published") {
      return null;
    }

    switch (status.toLowerCase()) {
      case "available":
        return <Badge className="bg-green-100 text-green-800">Available</Badge>;
      case "occupied":
        return <Badge className="bg-red-100 text-red-800">Occupied</Badge>;
      case "booked":
        return <Badge className="bg-orange-100 text-orange-800">Booked</Badge>;
      case "maintenance":
        return <Badge className="bg-gray-100 text-gray-800">Maintenance</Badge>;
      default:
        return null; // Don't show badge for unknown status
    }
  };

  const ImageCarousel = ({ space, className = "" }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    // Get all images from the space
    // Filter out null, undefined, and empty strings
    const allImages = space.media?.images || (space.image ? [space.image] : []);
    const images = allImages.filter(img => img && typeof img === "string" && img.trim() !== "");
    const hasMultipleImages = images.length > 1;

    const goToNext = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    };

    const goToPrev = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
    };

    const goToImage = (index, e) => {
      e.stopPropagation();
      setCurrentImageIndex(index);
    };

    return (
      <div
        className={`relative w-full h-48 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {images.length > 0 ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={`${space.name} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={e => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-dashed border-blue-200"
              style={{ display: "none" }}
            >
              <Building2 className="w-12 h-12 text-blue-400" />
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-dashed border-blue-200">
            <Building2 className="w-12 h-12 text-blue-400" />
          </div>
        )}

        {/* Navigation Arrows - Show on hover if multiple images */}
        {hasMultipleImages && isHovered && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all z-10"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4 text-gray-800" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all z-10"
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4 text-gray-800" />
            </button>
          </>
        )}

        {/* Image Indicators - Show if multiple images */}
        {hasMultipleImages && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={e => goToImage(index, e)}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentImageIndex ? "bg-white w-6" : "bg-white/60 hover:bg-white/80 w-1.5"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-3 right-3 z-10">{getStatusBadge(space.status)}</div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {spaces.map(space => (
        <Card
          key={space.id}
          className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
          onClick={() => onSelectSpace(space)}
        >
          <ImageCarousel space={space} />

          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg line-clamp-2" title={space.name}>
                  {space.name}
                </CardTitle>
                <div className="flex items-center mt-1 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                  <span className="truncate" title={space.location}>
                    {space.location}
                  </span>
                </div>
              </div>
              <Badge variant="outline" className="flex-shrink-0">
                {space.type}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 pt-0">
            <div className="space-y-4 flex-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-600">
                  <Users className="w-4 h-4 mr-1" />
                  {space.capacity} people
                </div>
                <div className="font-semibold text-lg truncate ml-2">{space.pricing}</div>
              </div>

              {space.amenities && space.amenities.length > 0 && (
                <div className="flex flex-wrap gap-2 min-h-[2rem]">
                  {space.amenities.slice(0, 4).map((amenity, index) => (
                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs">
                      {getAmenityIcon(amenity)}
                      <span className="truncate max-w-[100px]">{getAmenityName(amenity)}</span>
                    </div>
                  ))}
                  {space.amenities.length > 4 && (
                    <div className="flex items-center px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                      +{space.amenities.length - 4} more
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                className="flex-1 whitespace-nowrap text-xs"
                onClick={e => {
                  e.stopPropagation();
                  onSelectSpace(space);
                }}
                disabled={space.status !== "available"}
              >
                {space.status === "available" ? "Book Now" : "View Schedule"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap text-xs"
                onClick={e => {
                  e.stopPropagation();
                  onSelectSpace(space);
                }}
              >
                Details
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SpaceGridView;
